/**
 * 화면 오류 울타리
 * ---------------------------------------------------------------------------
 * 왜 필요한가
 *   React 는 렌더 중에 예외가 나면 **트리 전체를 떼어 낸다.** 울타리가 없으면
 *   화면이 하얗게 비고, 사용자는 "아무것도 안 나온다" 외에 할 말이 없다.
 *   실제로 지원 모드를 끝내고 기업 관리로 돌아올 때 그런 일이 있었고, 서버
 *   로그에는 호출이 한 건도 없어 원인을 찾는 데 한참 걸렸다.
 *
 *   여기서 받아 내면 적어도 **무엇이 터졌는지 화면에 남는다.** 사용자는 그
 *   문구를 그대로 전해 주면 되고, 나머지 화면(머리글·메뉴)은 살아 있어 다른
 *   일을 계속할 수 있다.
 *
 * 쓰는 곳
 *   AppShell 의 본문. 머리글·메뉴 바깥이 아니라 **본문만** 감싼다. 메뉴까지
 *   같이 죽으면 다른 화면으로 빠져나갈 길이 없다.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result, Typography } from 'antd';

const { Paragraph, Text } = Typography;

interface Props {
  children: ReactNode;
  /** 이 값이 바뀌면 오류 상태를 푼다. 보통 경로를 넘긴다. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props): void {
    // 다른 화면으로 옮겨 가면 다시 그려 볼 기회를 준다.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 콘솔에도 남긴다. 개발자 도구를 열어 둔 사람이 바로 볼 수 있어야 한다.
    console.error('[화면 오류]', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Result
        status="error"
        title="화면을 그리지 못했습니다"
        subTitle="아래 내용을 그대로 알려 주시면 원인을 찾을 수 있습니다."
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            새로고침
          </Button>
        }
      >
        <Paragraph>
          <Text code copyable style={{ whiteSpace: 'pre-wrap' }}>
            {error.message || String(error)}
          </Text>
        </Paragraph>
      </Result>
    );
  }
}
